import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import { debounce } from "lodash";
import resourcesReportApi from "../../store/services/resourcesReport.service";
import userService from "../../store/services/user.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import ReportFilterBar from "./components/ReportFilterBar";
import ReportKPICards from "./components/ReportKPICards";
import ReportCharts from "./components/ReportCharts";
import ReportTable from "./components/ReportTable";
import { KPICardsSkeleton, ChartsSkeleton, TableSkeleton } from "./components/ReportSkeletons";
import { UsersIcon } from "../dashboard/icons/icons";
import { ParentType, REPORT_COLUMNS, getLayoutName } from "./constants";
import { UserSearchRefType } from "../../helpers/utils";
import styles from "./ResourcesReport.module.scss";

function getSummaryPeriodRange(period) {
  const now = moment();
  switch (period) {
    case "last_week":
      return {
        startDate: now.clone().subtract(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().subtract(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
        label: "Last week",
      };
    case "this_week":
      return {
        startDate: now.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("isoWeek").format("YYYY-MM-DD"),
        label: "This week",
      };
    case "next_week":
      return {
        startDate: now.clone().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().add(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
        label: "Next week",
      };
    case "last_month":
      return {
        startDate: now.clone().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        label: "Last month",
      };
    case "this_month":
      return {
        startDate: now.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("month").format("YYYY-MM-DD"),
        label: "This month",
      };
    case "next_month":
      return {
        startDate: now.clone().add(1, "month").startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().add(1, "month").endOf("month").format("YYYY-MM-DD"),
        label: "Next month",
      };
    case "overall":
      return { startDate: null, endDate: null, label: "Overall" };
    default:
      return {
        startDate: now.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("isoWeek").format("YYYY-MM-DD"),
        label: "This week",
      };
  }
}

const PERIOD_OPTIONS = [
  { value: "last_week", label: "Last week" },
  { value: "this_week", label: "This week" },
  { value: "next_week", label: "Next week" },
  { value: "last_month", label: "Last month" },
  { value: "this_month", label: "This month" },
  { value: "next_month", label: "Next month" },
  { value: "overall", label: "Overall" },
  { value: "custom", label: "Custom" },
];

/**
 * ResourcesReport
 *
 * Props (optional – makes the component reusable from entity pages):
 * @param {string} parentType  - "client" | "portfolio" | "program" | "project"
 * @param {string} parentId    - GUID of the parent entity
 */
const ResourcesReport = ({ parentType, parentId, metaData }) => {
  const dispatch = useDispatch();

  // ─── Column / layout state ────────────────────────────────────────────────
  const layoutName = getLayoutName(parentType);
  const defaultLayout = REPORT_COLUMNS.map((c) => ({
    name: c.label,
    key: c.key,
    order: c.order,
    isVisible: c.isVisible,
    customName: "",
  }));
  const [columns, setColumns] = useState(defaultLayout);

  const debouncedSave = useRef(
    debounce((updatedColumns) => {
      userService
        .putUserLayoutRequest({ name: layoutName, columns: updatedColumns })
        .catch(() => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong saving layout. Please try again later.",
              type: "error",
            })
          );
        });
    }, 1000)
  );

  const fetchLayout = useCallback(() => {
    userService
      .getUserLayoutRequest(layoutName)
      .then((data) => {
        if (data?.columns?.length > 0) {
          const merged = defaultLayout.map((def) => {
            const saved = data.columns.find((s) => s.name === def.name);
            if (saved) {
              return { ...def, isVisible: saved.isVisible, customName: saved.customName || "", order: saved.order ?? def.order };
            }
            return def;
          });
          merged.sort((a, b) => a.order - b.order);
          setColumns(merged);
        }
      })
      .catch(() => { /* use defaults */ });
  }, [layoutName]);

  const handleColumnStateChange = useCallback((name) => {
    setColumns((prev) => {
      const updated = prev.map((col) =>
        col.name === name ? { ...col, isVisible: !col.isVisible } : col
      );
      debouncedSave.current(updated);
      return updated;
    });
  }, []);

  const handleHideColumnByKey = useCallback((key) => {
    setColumns((prev) => {
      const updated = prev.map((col) =>
        col.key === key ? { ...col, isVisible: false } : col
      );
      debouncedSave.current(updated);
      return updated;
    });
  }, []);

  const handleColumnLabelChange = useCallback((name, customName) => {
    setColumns((prev) => {
      const updated = prev.map((col) =>
        col.name === name ? { ...col, customName } : col
      );
      debouncedSave.current(updated);
      return updated;
    });
  }, []);

  const handleResetColumns = useCallback(() => {
    setColumns(defaultLayout);
    debouncedSave.current(defaultLayout);
  }, []);

  // ─── Filter state ─────────────────────────────────────────────────────────
  const [summaryPeriod, setSummaryPeriod] = useState("this_week");
  const [customDateRange, setCustomDateRange] = useState(null);
  // Single entity filter: { type: "client"|"portfolio"|"program"|"project", id, name } or null
  const [entityFilter, setEntityFilter] = useState(null);
  const [resourceFilter, setResourceFilter] = useState([]);

  // ─── Data state ───────────────────────────────────────────────────────────
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Collapse toggles ────────────────────────────────────────────────────
  const [showSummary, setShowSummary] = useState(true);

  // ─── Derived date range ───────────────────────────────────────────────────
  const selectedRange = useMemo(() => {
    if (summaryPeriod === "custom" && customDateRange) {
      return customDateRange;
    }
    return getSummaryPeriodRange(summaryPeriod);
  }, [summaryPeriod, customDateRange]);

  // Overall date range derived from response data (display only, not used for API calls)
  const overallDateRange = useMemo(() => {
    if (summaryPeriod === "overall" && reportData?.consolidated?.dailyBreakdown?.length > 0) {
      const dates = reportData.consolidated.dailyBreakdown.map((d) => d.date).sort();
      return { startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    return null;
  }, [summaryPeriod, reportData]);

  // ─── Build API filters ───────────────────────────────────────────────────
  const buildFilters = useCallback(() => {
    const filters = {};

    // Parent entity passed as prop
    if (parentType && parentId) {
      switch (parentType) {
        case ParentType.CLIENT:
          filters.clientId = parentId;
          break;
        case ParentType.PORTFOLIO:
          filters.portfolioId = parentId;
          break;
        case ParentType.PROGRAM:
          filters.programId = parentId;
          break;
        case ParentType.PROJECT:
          filters.projectId = parentId;
          break;
        default:
          break;
      }
    }

    // Entity filter (adds to / overrides parent when set)
    if (entityFilter) {
      switch (entityFilter.type) {
        case ParentType.CLIENT:
          filters.clientId = entityFilter.id;
          break;
        case ParentType.PORTFOLIO:
          filters.portfolioId = entityFilter.id;
          break;
        case ParentType.PROGRAM:
          filters.programId = entityFilter.id;
          break;
        case ParentType.PROJECT:
          filters.projectId = entityFilter.id;
          break;
        default:
          break;
      }
    }

    // Resource filter
    if (resourceFilter.length > 0) {
      filters.resources = resourceFilter.map((r) => r.id);
    }

    // Dates
    if (selectedRange.startDate && selectedRange.endDate) {
      filters.startDate = selectedRange.startDate;
      filters.endDate = selectedRange.endDate;
    }

    return filters;
  }, [parentType, parentId, entityFilter, resourceFilter, selectedRange]);

  // ─── Fetch report ─────────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const filters = buildFilters();
      const response = await resourcesReportApi.getReport(filters);
      setReportData(response || null);
    } catch {
      dispatch(
        generalActions.showToast({
          title: "Failed to load resources report.",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [buildFilters, dispatch]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  // ─── Extracted data ───────────────────────────────────────────────────────

  const items = reportData?.items || [];
  const consolidated = reportData?.consolidated || {};

  const settings = JSON.parse(localStorage.getItem("settings"));

  const showEntityFilters = !parentType || parentType !== ParentType.PROJECT;

  // Determine which entity types are available for filtering based on parent
  const availableEntityTypes = useMemo(() => {
    switch (parentType) {
      case ParentType.CLIENT:
        return ["portfolio", "program", "project"];
      case ParentType.PORTFOLIO:
        return ["program", "project"];
      case ParentType.PROGRAM:
        return ["project"];
      case ParentType.PROJECT:
        return [];
      default:
        return ["client", "portfolio", "program", "project"];
    }
  }, [parentType]);

  // UserSelect ref config — dynamic based on entityFilter, with metaData fallback
  const userSelectRef = useMemo(() => {
    // Project filter or project parent → use project as reference
    if (entityFilter?.type === ParentType.PROJECT) {
      return { refType: UserSearchRefType.Project, refId: entityFilter.id };
    }
    if (parentType === ParentType.PROJECT) {
      return { refType: UserSearchRefType.Project, refId: parentId };
    }

    // Client filter → use that client
    if (entityFilter?.type === ParentType.CLIENT) {
      return { refType: UserSearchRefType.Client, refId: entityFilter.id };
    }
    // Portfolio/program filter → use the client from the filter result
    if (entityFilter?.client?.id) {
      return { refType: UserSearchRefType.Client, refId: entityFilter.client.id };
    }

    // Parent is client → use it directly
    if (parentType === ParentType.CLIENT) {
      return { refType: UserSearchRefType.Client, refId: parentId };
    }
    // Fallback to metaData client
    if (metaData?.client?.id) {
      return { refType: UserSearchRefType.Client, refId: metaData.client.id };
    }

    return { refType: null, refId: null };
  }, [parentType, parentId, metaData, entityFilter]);

  return (
    <div className={styles.reportContainer}>
      <ReportFilterBar
        // Period
        summaryPeriod={summaryPeriod}
        onPeriodChange={setSummaryPeriod}
        selectedRange={overallDateRange || selectedRange}
        customDateRange={customDateRange}
        onCustomDateRangeChange={setCustomDateRange}
        periodOptions={PERIOD_OPTIONS}
        // Entity filter (hidden when parent is project)
        showEntityFilters={showEntityFilters}
        entityFilter={entityFilter}
        onEntityFilterChange={(filter) => {
          setEntityFilter(filter);
          setResourceFilter([]);
        }}
        availableEntityTypes={availableEntityTypes}
        parentType={parentType}
        parentId={parentId}
        // Resource filter
        resourceFilter={resourceFilter}
        onResourceFilterChange={setResourceFilter}
        userSelectRef={userSelectRef}
        // Summary toggle
        showSummary={showSummary}
        onToggleSummary={() => setShowSummary((p) => !p)}
        // Columns
        columns={columns}
        onColumnStateChange={handleColumnStateChange}
        onColumnLabelChange={handleColumnLabelChange}
        onResetColumns={handleResetColumns}
        // Title
        title={"Resources Report"}
        // Loading
        loading={loading}
      />

      <div className={styles.reportBody}>
        {showSummary && (
          <div className={styles.summarySection}>
            {loading ? (
              <>
                <KPICardsSkeleton count={columns.filter((c) => c.isVisible).length || 14} />
                <ChartsSkeleton />
              </>
            ) : (
              <>
                <ReportKPICards consolidated={consolidated} settings={settings} columns={columns} onHideColumn={handleHideColumnByKey} />
                {consolidated.dailyBreakdown?.length > 0 && (
                  <ReportCharts dailyBreakdown={consolidated.dailyBreakdown} settings={settings} columns={columns} />
                )}
              </>
            )}
          </div>
        )}

        {/* Table section */}
        {loading ? (
          <TableSkeleton />
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            <UsersIcon />
            <p>No resources found for the selected filters.</p>
          </div>
        ) : (
          <ReportTable items={items} settings={settings} chartsVisible={showSummary} columns={columns} />
        )}
      </div>
    </div>
  );
};

export default ResourcesReport;
